import { Download, ChevronDown, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAnalyticsOverview } from '@/hooks/useAnalyticsOverview'
import { useBranch } from '@/providers/branch-provider'
import { formatCurrency } from '@/lib/utils'

export function RevenueChart() {
  const { branchId } = useBranch()

  const currentYear = new Date().getFullYear()
  const startDate = new Date(currentYear, 0, 1)
  const endDate = new Date(currentYear, 11, 31)

  const { data, isPending, isError } = useAnalyticsOverview({
    branchId: branchId === 'all' ? undefined : branchId,
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  })

  const chartData = data?.charts?.revenueTrend ?? []
  const maxValue = chartData.length > 0 ? Math.max(...chartData.map((d) => d.revenue)) : 100

  // Format month label (e.g., "Jan 2025" -> "Jan")
  const formatMonth = (label: string) => {
    const parts = label.split(' ')
    return parts[0] // Just the month abbreviation
  }

  return (
    <div className="bg-card rounded-xl p-4 sm:p-7">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <span className="text-sm sm:text-[15px] font-semibold text-text-dark">Revenue over time</span>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border border-border rounded-full bg-card text-xs sm:text-sm font-medium text-text-dark cursor-pointer transition-colors hover:bg-hover">
            Month
            <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-text-secondary" />
          </button>
          <button className="w-9 h-9 sm:w-[42px] sm:h-[42px] border border-border rounded-full bg-card cursor-pointer flex items-center justify-center transition-colors hover:bg-hover">
            <Download className="w-4 h-4 sm:w-5 sm:h-5 text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 sm:gap-5 mb-4 sm:mb-7">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-text-secondary font-medium">
          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-blue-dark" />
          Putrajaya
        </div>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-text-secondary font-medium">
          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-blue-light" />
          Shah Alam
        </div>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-text-secondary font-medium">
          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-success" />
          Bangi
        </div>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-text-secondary font-medium">
          <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-purple-500" />
          Kuala Lumpur
        </div>
      </div>

      {/* Chart Area */}
      <div className="overflow-x-auto overflow-y-visible -mx-4 px-4 sm:mx-0 sm:px-0">
        {isPending ? (
          <div className="h-[180px] sm:h-[220px] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        ) : isError ? (
          <div className="h-[180px] sm:h-[220px] flex items-center justify-center text-text-muted text-sm">
            Failed to load data
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[180px] sm:h-[220px] flex items-center justify-center text-text-muted text-sm">
            No data available
          </div>
        ) : (
          <div className="h-[180px] sm:h-[220px] flex items-end justify-between pb-6 sm:pb-8 relative min-w-[480px] sm:min-w-0">
            {/* Grid lines */}
            <div className="absolute inset-0 bottom-6 sm:bottom-8 flex flex-col justify-between pointer-events-none">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="w-full border-t border-border-light opacity-50" />
              ))}
            </div>

            {/* Bars */}
            {chartData.map((d, i) => {
              const barHeight = maxValue > 0 ? (d.revenue / maxValue) * 140 : 0
              return (
                <div key={d.month} className="flex flex-col items-center gap-2 sm:gap-3 flex-1 relative z-10">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${barHeight}px` }}
                    transition={{ delay: i * 0.05, duration: 0.4, ease: 'easeOut' }}
                    className="w-6 sm:w-9 rounded-t-md cursor-pointer transition-opacity hover:opacity-85"
                    style={{
                      background: 'linear-gradient(180deg, var(--color-blue-dark) 0%, var(--color-blue-light) 100%)',
                    }}
                    title={`${d.label}: ${formatCurrency(d.revenue)}`}
                  />
                  <span className="text-[11px] sm:text-[13px] text-text-muted font-medium">
                    {formatMonth(d.label)}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Total Revenue Summary */}
      {data && chartData.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-sm">
          <span className="text-text-muted">Total {currentYear}</span>
          <span className="font-semibold text-text-dark">
            {formatCurrency(chartData.reduce((sum, d) => sum + d.revenue, 0))}
          </span>
        </div>
      )}
    </div>
  )
}
